import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faTimes, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';

interface ReminderWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  supplierName: string;
  daysSinceLastReminder: number;
  lastReminderDate: Date;
}

const ReminderWarningModal: React.FC<ReminderWarningModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  supplierName,
  daysSinceLastReminder,
  lastReminderDate
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-orange-50 px-6 py-4 border-b border-orange-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon 
                      icon={faExclamationTriangle} 
                      className="h-6 w-6 text-orange-600" 
                    />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-orange-900">
                      Erinnerung bereits gesendet
                    </h3>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-orange-400 hover:text-orange-600 transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Eine Erinnerung wurde bereits vor kurzem an{' '}
                  <span className="font-medium text-gray-900">{supplierName}</span>{' '}
                  gesendet.
                </p>
              </div>

              {/* Warning Details */}
              <div className="bg-orange-50 rounded-lg p-4 mb-4">
                <div className="flex items-center mb-2">
                  <FontAwesomeIcon icon={faCalendarAlt} className="h-4 w-4 text-orange-600 mr-2" />
                  <span className="text-sm font-medium text-orange-900">
                    Letzte Erinnerung
                  </span>
                </div>
                <div className="text-sm text-orange-800">
                  <p>
                    <strong>Datum:</strong> {lastReminderDate.toLocaleDateString('de-DE', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  <p>
                    <strong>Vor:</strong> {daysSinceLastReminder} Tag{daysSinceLastReminder !== 1 ? 'en' : ''}
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4 mr-2" />
                  Mehrfache Erinnerungen können beim Lieferanten zu Verwirrung führen.
                </p>
              </div>

              <p className="text-sm text-gray-600">
                Möchten Sie trotzdem eine weitere Erinnerung senden?
              </p>
            </div>

            {/* Actions */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors"
              >
                Trotzdem senden
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ReminderWarningModal;
